import { createContext, useContext } from 'react';
import { v4 } from 'uuid';

var FieldType;
(function (FieldType) {
    FieldType["Number"] = "Number";
    FieldType["String"] = "String";
    FieldType["Structure"] = "Structure";
    FieldType["ID"] = "ID";
    // Computed = 'Computed', deprecated
    FieldType["Date"] = "Date";
})(FieldType || (FieldType = {}));

var ExpressionType;
(function (ExpressionType) {
    ExpressionType["Identifier"] = "Identifier";
    ExpressionType["BinaryExpression"] = "BinaryExpression";
    ExpressionType["CallExpression"] = "CallExpression";
    ExpressionType["Literal"] = "Literal";
    ExpressionType["LogicalExpression"] = "LogicalExpression";
    ExpressionType["UnaryExpression"] = "UnaryExpression";
})(ExpressionType || (ExpressionType = {}));

var DesirabilityFunctionType;
(function (DesirabilityFunctionType) {
    DesirabilityFunctionType["linear"] = "linear";
    DesirabilityFunctionType["unitStep"] = "unitStep";
    DesirabilityFunctionType["rectangular"] = "rectangular";
    DesirabilityFunctionType["triangular"] = "triangular";
    DesirabilityFunctionType["logarithmic"] = "logarithmic";
    DesirabilityFunctionType["sigmoid"] = "sigmoid";
    DesirabilityFunctionType["custom"] = "custom";
    DesirabilityFunctionType["discrete"] = "discrete";
})(DesirabilityFunctionType || (DesirabilityFunctionType = {}));

const EMPTY_DECISION_TREE = {
    name: '',
    data: {
        layout: 'Auto',
        nodes: [],
        arrows: [],
    },
};

var ReasonType;
(function (ReasonType) {
    ReasonType["DoNotCalculate"] = "DoNotCalculate";
    ReasonType["FilteredOff"] = "FilteredOff";
    ReasonType["ReplacedWithX"] = "ReplacedWithX";
    ReasonType["ReplacedWithY"] = "ReplacedWithY";
    ReasonType["ReplacedWithColumn"] = "ReplacedWithColumn";
})(ReasonType || (ReasonType = {}));

var ResponseErrors;
(function (ResponseErrors) {
    ResponseErrors["NOT_FOUND"] = "NOT_FOUND";
    ResponseErrors["UNKNOWN"] = "UNKNOWN";
})(ResponseErrors || (ResponseErrors = {}));

const MoosaDataContext = createContext(null);
function useMoosaDataContext() {
    const context = useContext(MoosaDataContext);
    if (!context) {
        throw Error('IMoosaDataService is not provided in MoosaDataContext');
    }
    return context;
}

var SpecialColumn;
(function (SpecialColumn) {
    SpecialColumn["RowNumber"] = "RowNumber";
    SpecialColumn["Score"] = "Score";
    SpecialColumn["Radar"] = "Radar";
    SpecialColumn["NotUsed"] = "NotUsed";
    SpecialColumn["Missing"] = "Missing";
    SpecialColumn["Comparison"] = "Comparison";
})(SpecialColumn || (SpecialColumn = {}));
function isScoringType(type, isDiscreteColumn) {
    return type === FieldType.Number || (type === FieldType.String && !!isDiscreteColumn);
}
function isScoringColumn(col) {
    return isScoringType(col?.type, col?.isDiscreteColumn);
}
function isNumericColumn(col) {
    return col?.type === FieldType.Number;
}

var DatasetMutColumnType;
(function (DatasetMutColumnType) {
    DatasetMutColumnType["CID"] = "CID";
    DatasetMutColumnType["string"] = "string";
    DatasetMutColumnType["number"] = "number";
    DatasetMutColumnType["arrayOfNumber"] = "arrayOfNumber";
    DatasetMutColumnType["boolean"] = "boolean";
    DatasetMutColumnType["datetime"] = "datetime";
    DatasetMutColumnType["molecule"] = "molecule";
    DatasetMutColumnType["tags"] = "tags";
    // The column exists only in config, not in dataset. The column has subtype
    DatasetMutColumnType["virtual"] = "virtual";
})(DatasetMutColumnType || (DatasetMutColumnType = {}));
const COLUMN_TYPE_READABLE_NAME = {
    [DatasetMutColumnType.arrayOfNumber]: 'Array of numbers',
    [DatasetMutColumnType.tags]: 'Tags',
    [DatasetMutColumnType.boolean]: 'Boolean',
    [DatasetMutColumnType.CID]: 'CID',
    [DatasetMutColumnType.string]: 'String',
    [DatasetMutColumnType.datetime]: 'Datetime',
    [DatasetMutColumnType.molecule]: 'Molecule',
    [DatasetMutColumnType.number]: 'Number',
    [DatasetMutColumnType.virtual]: 'Virtual',
};
const getRowSelectionKey = (rowId, groupId) => {
    return (groupId !== undefined ? `${rowId}:${groupId}` : `${rowId}`);
};
/**
 *  TYPE GUARDS
 */
function isColumnOfType(metadata, columnType) {
    return metadata?.type === columnType;
}
function isPossiblyDiscreteColumn(metadata) {
    if (!metadata)
        return false;
    return (metadata.type === DatasetMutColumnType.string ||
        metadata.type === DatasetMutColumnType.number ||
        metadata.type === DatasetMutColumnType.boolean ||
        metadata.type === DatasetMutColumnType.tags);
}
function isPossiblyComputedColumn(metadata) {
    if (!metadata)
        return false;
    return (metadata.type === DatasetMutColumnType.string ||
        metadata.type === DatasetMutColumnType.number ||
        metadata.type === DatasetMutColumnType.boolean ||
        metadata.type === DatasetMutColumnType.datetime);
}

var EMoosaSarConfigImageSize;
(function (EMoosaSarConfigImageSize) {
    EMoosaSarConfigImageSize["Small"] = "small";
    EMoosaSarConfigImageSize["Medium"] = "medium";
    EMoosaSarConfigImageSize["Large"] = "large";
    EMoosaSarConfigImageSize["ExtraLarge"] = "extraLarge";
})(EMoosaSarConfigImageSize || (EMoosaSarConfigImageSize = {}));

var JobStatus;
(function (JobStatus) {
    JobStatus["NotStarted"] = "";
    JobStatus["Started"] = "Started";
    JobStatus["Finished"] = "Finished";
    JobStatus["Failed"] = "Failed";
    JobStatus["Deleted"] = "Deleted";
})(JobStatus || (JobStatus = {}));

var SarMatrixCellView;
(function (SarMatrixCellView) {
    SarMatrixCellView["Circles"] = "Circles";
    SarMatrixCellView["PieChart"] = "PieChart";
    SarMatrixCellView["FactorsAbsolute"] = "FactorsAbsolute";
})(SarMatrixCellView || (SarMatrixCellView = {}));

var MMPView;
(function (MMPView) {
    MMPView["Table"] = "Table";
    MMPView["RadiantChart"] = "Radiant Chart";
    MMPView["MultipleRadiantChart"] = "Multiple Radiant Chart";
})(MMPView || (MMPView = {}));

/**
 *
 * @returns
 *  value in [0, 1],
 *  or null - not calculated due to missing values,
 *  or undefined - yet not calculated
 */
function getSubstanceScoreValue(scores, rowId) {
    return scores[rowId]?.value;
}
/**
 * Factor = weight * desirability_function(property_value)
 * columnIndex -  the index of the column in the dataset columns array
 */
function getScoringFactorByColumnId(scores, columnIndexes, rowId, colId) {
    const columnIndex = columnIndexes[colId];
    return scores[rowId]?.factors[columnIndex];
}
/**
 * Raw Factor = desirability_function(property_value)
 * columnIndex -  the index of the column in the dataset columns array
 */
function getRawScoringFactorByColumnId(scores, columnIndexes, rowId, colId) {
    const columnIndex = columnIndexes[colId];
    return scores[rowId]?.rawValues[columnIndex];
}
/**
 * Convert column indexes in scoring factors to corresponding columnIds
 */
function getRawScoringFactorsWithColumnIds(columnIds, score) {
    return Object.keys(score?.rawValues ?? {}).map((columnIndex) => [
        columnIds[+columnIndex],
        score?.rawValues[+columnIndex],
    ]);
}

var SortFunctionTableType;
(function (SortFunctionTableType) {
    SortFunctionTableType["Ascending"] = "Ascending";
    SortFunctionTableType["Descending"] = "Descending";
    SortFunctionTableType["None"] = "None";
})(SortFunctionTableType || (SortFunctionTableType = {}));

function toLine$1(points, range) {
    const line = points.reduce((acc, point, index) => {
        const fragment = [
            {
                ...point,
                // to differentiate from original point
                id: v4(),
                x: index,
                y: 0,
                mark: false,
                originalPointIndex: point.isNotInFunction ? undefined : point.originalPointIndex, // to not allow to move the point
            },
            {
                ...point,
                x: index,
                originalPointIndex: point.isNotInFunction ? undefined : point.originalPointIndex,
            },
            {
                ...point,
                // to differentiate from original point
                id: v4(),
                x: index,
                y: 0,
                mark: false,
                originalPointIndex: point.isNotInFunction ? undefined : point.originalPointIndex,
            },
        ];
        acc.push(...fragment);
        return acc;
    }, []);
    return extendLineToRange$1(line, range);
}
function convertToServerNeededFormat(points) {
    return points
        .filter((point) => !point.isNotInFunction)
        .map(({ x, y }) => ({
        x,
        y,
    }));
}
/**
 * this function mutate points array
 */
function extendLineToRange$1(points, range) {
    const first = points[0];
    const last = points[points.length - 1];
    // @ts-ignore
    if (range.min < first.x) {
        points.unshift({
            id: v4(),
            x: range.min,
            y: first.y,
        });
    }
    // @ts-ignore
    if (range.max > last.x) {
        points.push({
            id: v4(),
            x: range.max,
            y: last.y,
        });
    }
    return points;
}
function updateLinePoint$1(points, { originalPointIndex, y: newY }) {
    const pnts = [...points];
    const y = checkYBoundaries$1(newY);
    const point = pnts.find((apoint) => apoint.originalPointIndex === originalPointIndex);
    point.y = y;
    return { points: pnts };
}
const X_MIN$1 = -1e24;
const X_MAX$1 = 1e24;
function checkXBoundaries$1(x) {
    // @ts-ignore
    if (x < X_MIN$1)
        return X_MIN$1;
    // @ts-ignore
    if (x > X_MAX$1)
        return X_MAX$1;
    return x;
}
function checkYBoundaries$1(y) {
    if (y < 0) {
        return 0;
    }
    if (y > 1) {
        return 1;
    }
    return y;
}

let initialIdCounter = 1;
function sortPoints(type, points, xSort, ySort) {
    if (type === FieldType.Number) {
        points.sort((point, nextPoint) => {
            if (xSort === SortFunctionTableType.Ascending) {
                return Number(point.x) - Number(nextPoint.x);
            }
            else if (xSort === SortFunctionTableType.Descending) {
                return Number(nextPoint.x) - Number(point.x);
            }
            else if (ySort === SortFunctionTableType.Descending) {
                return Number(nextPoint.y) - Number(point.y);
            }
            else if (ySort === SortFunctionTableType.Ascending) {
                return Number(point.y) - Number(nextPoint.y);
            }
            else {
                return 0;
            }
        });
    }
    if (type === FieldType.String) {
        points.sort(function (point, nextPoint) {
            if (xSort !== SortFunctionTableType.None) {
                const nameA = point.x.toString().toLowerCase();
                const nameB = nextPoint.x.toString().toLowerCase();
                if (xSort === SortFunctionTableType.Ascending) {
                    if (nameA < nameB) {
                        return -1;
                    }
                    if (nameA > nameB) {
                        return 1;
                    }
                    return 0;
                }
                else if (xSort === SortFunctionTableType.Descending) {
                    if (nameA > nameB) {
                        return -1;
                    }
                    if (nameA < nameB) {
                        return 1;
                    }
                    return 0;
                }
            }
            else if (ySort !== SortFunctionTableType.None) {
                if (ySort === SortFunctionTableType.Ascending) {
                    return Number(point.y) - Number(nextPoint.y);
                }
                else if (ySort === SortFunctionTableType.Descending) {
                    return Number(nextPoint.y) - Number(point.y);
                }
                else {
                    return 0;
                }
            }
            return 0;
        });
    }
    return points;
}
const discreteFunctionRules = {
    init(metadata) {
        if (metadata.isDiscreteColumn) {
            const initPoints = metadata.statistics.textCategories.map((point) => {
                const discretePoint = {
                    id: v4(),
                    x: point.value,
                    y: 0,
                    originalPointIndex: initialIdCounter++,
                    count: point.count,
                    isAddedByUser: false,
                    isNotInFunction: false,
                    mark: true,
                };
                return discretePoint;
            });
            const params = {
                points: sortPoints(metadata.type, initPoints, SortFunctionTableType.Ascending, SortFunctionTableType.None),
            };
            return params;
        }
        return null;
    },
    getValue(xVal, params) {
        const point = params.points.find(({ x }) => x.toString().toLowerCase() === xVal.toString().toLowerCase());
        return point === undefined ? 0 : point.y;
    },
    getRange: function (params, metadata) {
        throw new Error('Function is not applicable to the discrete values.');
    },
    toLine(params, range) {
        return toLine$1(params.points, range);
    },
    toServerTemplate(params) {
        return convertToServerNeededFormat(params.points);
    },
    fromServerTemplate(points, metadata) {
        // todo check that it is correct column type
        const textCategories = metadata?.statistics?.textCategories || [];
        const pointsMap = new Map();
        points.forEach((point) => {
            // initialize like the point exists in function and absent in dataset
            const discretePoint = {
                id: v4(),
                x: point.x,
                y: point.y,
                originalPointIndex: initialIdCounter++,
                count: 0,
                isAddedByUser: true,
                isNotInFunction: false,
                mark: true,
            };
            pointsMap.set(String(point.x), discretePoint);
        });
        textCategories.forEach((category) => {
            const point = pointsMap.get(String(category.value));
            if (point) {
                // point both in function and in dataset
                point.count = category.count;
                point.isAddedByUser = false;
            }
            else {
                // point exists in dataset and absent in function
                const discretePointNotInFunction = {
                    id: v4(),
                    x: category.value,
                    y: 0,
                    originalPointIndex: initialIdCounter++,
                    count: category.count,
                    isAddedByUser: false,
                    isNotInFunction: true,
                    mark: false,
                };
                pointsMap.set(String(category.value), discretePointNotInFunction);
            }
        });
        const discretePoints = [...pointsMap.values()];
        return {
            points: discretePoints,
        };
    },
    movePoint(params, point) {
        return updateLinePoint$1(params.points, point);
    },
    addPoint(params, point, fieldType, xSort, ySort) {
        const newParams = {
            points: sortPoints(fieldType, [
                ...params.points,
                {
                    id: v4(),
                    x: checkXBoundaries$1(point.x),
                    y: checkYBoundaries$1(point.y),
                    originalPointIndex: initialIdCounter++,
                    isAddedByUser: true,
                    count: 0,
                    mark: true,
                },
            ], xSort, ySort),
        };
        return newParams;
    },
    removePoint(params, index) {
        const pointToRemove = params.points.find((point) => point.originalPointIndex === index);
        const newParams = {
            points: params.points.filter((point) => point !== pointToRemove),
        };
        return newParams;
    },
};

function pointsCompare(p1, p2) {
    return p1.x === p2.x ? 0 : p1.x > p2.x ? 1 : -1;
}
function getValueFromLinearApprox(xVal, points) {
    const sortedPoints = [...points].sort(pointsCompare);
    const num = sortedPoints.length - 1;
    for (let i = 0; i < num; i++) {
        const point = sortedPoints[i];
        const next = sortedPoints[i + 1];
        if (point.x >= xVal) {
            return point.y;
        }
        if (point.x <= xVal && xVal < next.x) {
            const fraction = (xVal - point.x) / (next.x - point.x);
            return point.y + (next.y - point.y) * fraction;
        }
    }
    return sortedPoints[num].y;
}
function toLine(points, range) {
    const sortedPoints = [...points].sort(pointsCompare);
    const line = sortedPoints.map((point) => ({
        ...point,
        mark: true,
    }));
    return extendLineToRange(line, range);
}
function removeIndices(points) {
    return points.map(({ x, y, id }) => ({
        x,
        y,
    }));
}
function assignIndices(points) {
    return points.map((point, originalPointIndex) => ({
        ...point,
        originalPointIndex,
        id: v4(),
    }));
}
/**
 * this function mutate points array
 */
function extendLineToRange(points, range) {
    const first = points[0];
    const last = points[points.length - 1];
    if (range.min < first.x) {
        points.unshift({
            id: v4(),
            x: range.min,
            y: first.y,
        });
    }
    if (range.max > last.x) {
        points.push({
            id: v4(),
            x: range.max,
            y: last.y,
        });
    }
    return points;
}
/**
 * To cover a case when statistics.min === statistics.max
 * (A dataset with one point, f. e.)
 * @param val
 */
function getRangeForSingleVal(val) {
    const delta = val === 0 ? 1 : Math.max(1, Math.abs(val / 10));
    return {
        min: val - delta,
        max: val + delta,
    };
}
/**
 * avoid same min and max
 * @param metadata
 */
function getRangeForInitialization(metadata) {
    const min = metadata.statistics?.min || 0;
    const max = metadata.statistics?.max || 0;
    if (min === max) {
        return getRangeForSingleVal(min);
    }
    return { min, max };
}
function getRangeForLinearApprox(points, metadata) {
    const sortedPoints = [...points].sort(pointsCompare);
    const range = {
        min: Math.min(sortedPoints[0].x, metadata.statistics.min),
        max: Math.max(sortedPoints[sortedPoints.length - 1].x, metadata.statistics.max),
    };
    if (range.max === range.min) {
        return getRangeForSingleVal(range.max);
    }
    return range;
}
function updateLinePoint(points, { originalPointIndex, x: newX, y: newY }) {
    const pnts = [...points];
    const x = checkXBoundaries(newX);
    const y = checkYBoundaries(newY);
    const point = pnts.find((apoint) => apoint.originalPointIndex === originalPointIndex);
    point.x = x;
    // one of the points should stick to y:1
    if (point.y === 1) {
        // has more points at y:1
        // allow moving
        if (pnts.some((aPoint) => aPoint !== point && aPoint.y === 1)) {
            point.y = y;
        }
    }
    else {
        // y < 1 - allow moving
        point.y = y;
    }
    return { points: pnts };
}
const X_MIN = -1e24;
const X_MAX = 1e24;
function checkXBoundaries(x) {
    if (x < X_MIN) {
        return X_MIN;
    }
    if (x > X_MAX) {
        return X_MAX;
    }
    return x;
}
function checkYBoundaries(y) {
    if (y < 0) {
        return 0;
    }
    if (y > 1) {
        return 1;
    }
    return y;
}
const MAX_ERROR = 0.005;
/**
 * split range into segments to minimize error when approximate function by linear intervals
 *
 * @param initialPoints - array of initial points
 * @param fx - function f(x), we expect that f(x) has same sign of first derivative on each interval
 * @param dfx - derivative of f(x)
 */
function segmentation(initialPoints, fx, dfx) {
    const points = [...initialPoints].sort(pointsCompare);
    const rangesStack = [];
    for (let i = 0; i < points.length - 1; i++) {
        rangesStack.push([points[i], points[i + 1]]);
    }
    while (rangesStack.length) {
        const [p0, p1] = rangesStack.pop();
        const delta = p1.y - p0.y;
        const deltaApprox = dfx(p0.x) * (p1.x - p0.x);
        if (Math.abs(deltaApprox - delta) > MAX_ERROR) {
            const x = (p0.x + p1.x) / 2;
            const middle = {
                id: v4(),
                x,
                y: fx(x),
            };
            rangesStack.push([p0, middle]);
            rangesStack.push([middle, p1]);
            points.push(middle);
        }
    }
    return points.sort(pointsCompare);
}

const customCurveFunctionRules = {
    init(metadata) {
        const params = {
            points: [
                { x: metadata.statistics.min, y: 0, originalPointIndex: 0, id: v4() },
                { x: metadata.statistics.max, y: 1, originalPointIndex: 1, id: v4() },
            ],
        };
        return params;
    },
    getValue(xVal, params) {
        return getValueFromLinearApprox(xVal, params.points);
    },
    getRange(params, metadata) {
        return getRangeForLinearApprox(params.points, metadata);
    },
    toLine(params, range) {
        return toLine(params.points, range);
    },
    toServerTemplate(params) {
        return removeIndices(params.points);
    },
    fromServerTemplate(points) {
        return {
            points: assignIndices(points),
        };
    },
    movePoint(params, point) {
        return updateLinePoint(params.points, point);
    },
    addPoint(params, point) {
        const newParams = {
            points: [
                ...params.points,
                {
                    x: checkXBoundaries(point.x),
                    y: checkYBoundaries(point.y),
                    originalPointIndex: point.originalPointIndex,
                    id: v4(),
                },
            ].sort(pointsCompare),
        };
        return newParams;
    },
    removePoint(params, index) {
        if (params.points.length === 2) {
            return params;
        }
        const pointToRemove = params.points.find((point) => point.originalPointIndex === index);
        if (pointToRemove.y === 1) {
            // check if there are more points with y = 1
            if (!params.points.some((point) => point !== pointToRemove && point.y === 1)) {
                return params;
            }
        }
        const newParams = {
            points: params.points.filter((point) => point !== pointToRemove),
        };
        return newParams;
    },
};

const linearFunctionRules = {
    init(metadata) {
        const { min, max } = getRangeForInitialization(metadata);
        return {
            points: [
                { x: min, y: 0, originalPointIndex: 0, id: v4() },
                { x: max, y: 1, originalPointIndex: 1, id: v4() },
            ],
        };
    },
    getValue(xVal, params) {
        return getValueFromLinearApprox(xVal, params.points);
    },
    getRange(params, metadata) {
        return getRangeForLinearApprox(params.points, metadata);
    },
    toLine(params, range) {
        return toLine(params.points, range);
    },
    toServerTemplate(params) {
        return removeIndices(params.points);
    },
    fromServerTemplate(points) {
        return { points: assignIndices(points) };
    },
    movePoint(params, point) {
        return updateLinePoint(params.points, point);
    },
};

/**
 * derivative for
 * Math.log( ((base - 1) * (xVal - params.xForY0)) / (params.xForY1 - params.xForY0) + 1 ) / Math.log(base)
 */
function logDerivative(val, params) {
    const { base, xForY0, xForY1 } = params;
    const _a = (base - 1) / (xForY1 - xForY0);
    const _b = xForY0;
    const _c = Math.log(base);
    return _a / (_a * _c * (val - _b) + _c);
}
const logarithmicFunctionRules = {
    init(metadata) {
        const params = {
            base: 2.718,
            xForY0: metadata.statistics.min,
            xForY1: metadata.statistics.min !== metadata.statistics.max
                ? metadata.statistics.max
                : metadata.statistics.min + 1,
        };
        return params;
    },
    getValue(xVal, params) {
        let minX, maxX, minY, maxY;
        if (params.xForY0 < params.xForY1) {
            minX = params.xForY0;
            minY = 0;
            maxX = params.xForY1;
            maxY = 1;
        }
        else {
            minX = params.xForY1;
            minY = 1;
            maxX = params.xForY0;
            maxY = 0;
        }
        if (xVal <= minX) {
            return minY;
        }
        if (xVal >= maxX) {
            return maxY;
        }
        const base = params.base === 1 ? 1.001 : params.base;
        return (Math.log(((base - 1) * (xVal - params.xForY0)) / (params.xForY1 - params.xForY0) + 1) / Math.log(base));
    },
    getRange(params, metadata) {
        const { min, max } = metadata.statistics;
        return {
            min: Math.min(min, params.xForY0, params.xForY1),
            max: Math.max(max, params.xForY0, params.xForY1),
        };
    },
    toLine(params, range) {
        const p0 = {
            id: 'first',
            x: params.xForY0,
            y: 0,
            originalPointIndex: 0,
            mark: true,
        };
        const p1 = {
            id: 'second',
            x: params.xForY1,
            y: 1,
            originalPointIndex: 1,
            mark: true,
        };
        const initialPoints = [p0, p1];
        const fx = (x) => logarithmicFunctionRules.getValue(x, params);
        const dfx = (x) => logDerivative(x, params);
        const points = segmentation(initialPoints, fx, dfx);
        // console.info('logarithm segmentation', points.length);
        return extendLineToRange(points, range);
    },
    toServerTemplate(params) {
        // TODO - a crutch: fix the crutch
        return [
            {
                x: params.xForY0,
                y: params.base,
            },
            {
                x: params.xForY1,
                y: 0,
            },
        ];
    },
    fromServerTemplate(points) {
        // TODO - and fix this as well
        return {
            base: points[0].y,
            xForY0: points[0].x,
            xForY1: points[1].x,
        };
    },
    movePoint(logParams, { originalPointIndex, x: newX }) {
        const params = { ...logParams };
        const x = checkXBoundaries(newX);
        if (originalPointIndex === 0) {
            params.xForY0 = x;
        }
        else if (originalPointIndex === 1) {
            params.xForY1 = x;
        }
        return params;
    },
};

const rectangularFunctionRules = {
    init(metadata) {
        const { min, max } = getRangeForInitialization(metadata);
        const third = (2 * min + max) / 3;
        const twoThird = (min + 2 * max) / 3;
        const params = {
            points: [
                { x: min, y: 0, originalPointIndex: 0, id: v4() },
                { x: third, y: 1, originalPointIndex: 1, id: v4() },
                { x: twoThird, y: 1, originalPointIndex: 2, id: v4() },
                { x: max, y: 0, originalPointIndex: 3, id: v4() },
            ],
        };
        return params;
    },
    getValue(xVal, params) {
        return getValueFromLinearApprox(xVal, params.points);
    },
    getRange(params, metadata) {
        return getRangeForLinearApprox(params.points, metadata);
    },
    toLine(params, range) {
        return toLine(params.points, range);
    },
    toServerTemplate(params) {
        return removeIndices(params.points);
    },
    fromServerTemplate(points) {
        return { points: assignIndices(points) };
    },
    movePoint(params, point) {
        return updateLinePoint(params.points, point);
    },
};

// calculate first derivation of sigmoid function
function derivateSigmoid(val, params) {
    const { l, k, xCenter } = params;
    const _p = Math.exp(-k * (val - xCenter));
    return (k * l * _p) / ((_p + 1) * (_p + 1));
}
const sigmoidFunctionRules = {
    init(metadata) {
        const params = {
            l: 1,
            k: 1,
            xCenter: (metadata.statistics.max + metadata.statistics.min) / 2,
        };
        return params;
    },
    getValue(xVal, params) {
        const { l, k, xCenter } = params;
        /// L / (1 + exp(-K * (x - xCenter)))
        const sigmoid = 1 / (1 + Math.exp(-k * (xVal - xCenter)));
        if (l >= 0) {
            return l * sigmoid;
        }
        return l * sigmoid - l;
    },
    getRange(params, metadata) {
        const delta = (metadata.statistics.max - metadata.statistics.min) / 20;
        const interestingRange = [
            { x: params.xCenter - delta, y: 0, id: v4() },
            { x: params.xCenter + delta, y: 0, id: v4() },
        ];
        return getRangeForLinearApprox(interestingRange, metadata);
    },
    toLine(params, range) {
        const p0 = {
            id: 'min',
            x: range.min,
            y: sigmoidFunctionRules.getValue(range.min, params),
        };
        const p1 = {
            id: 'max',
            x: range.max,
            y: sigmoidFunctionRules.getValue(range.max, params),
        };
        const pCenter = {
            id: 'center',
            x: params.xCenter,
            y: sigmoidFunctionRules.getValue(params.xCenter, params),
            mark: true,
            originalPointIndex: 0,
        };
        const initialPoints = range.min <= params.xCenter && params.xCenter <= range.max
            ? [p0, pCenter, p1]
            : [p0, p1];
        const fx = (x) => sigmoidFunctionRules.getValue(x, params);
        const dfx = (x) => derivateSigmoid(x, params);
        const line = segmentation(initialPoints, fx, dfx);
        // console.info('sigmoid segmentation', line.length);
        return line;
    },
    toServerTemplate(params) {
        // TODO - a crutch: fix the crutch
        return [
            { x: params.l, y: params.k },
            { x: params.xCenter, y: 0 },
        ];
    },
    fromServerTemplate(points) {
        // TODO - and fix this as well
        return {
            l: points[0].x,
            k: points[0].y,
            xCenter: points[1].x,
        };
    },
    movePoint(sigParams, { originalPointIndex, x }) {
        const params = { ...sigParams };
        // eslint-disable-next-line no-param-reassign
        x = checkXBoundaries(x);
        if (originalPointIndex === 0) {
            params.xCenter = x;
        }
        return params;
    },
};

const triangularFunctionRules = {
    init(metadata) {
        const { min, max } = getRangeForInitialization(metadata);
        const middle = (min + max) / 2;
        const params = {
            points: [
                { id: v4(), x: min, y: 0, originalPointIndex: 0 },
                { id: v4(), x: middle, y: 1, originalPointIndex: 1 },
                { id: v4(), x: max, y: 0, originalPointIndex: 2 },
            ],
        };
        return params;
    },
    getValue(xVal, params) {
        return getValueFromLinearApprox(xVal, params.points);
    },
    getRange(params, metadata) {
        return getRangeForLinearApprox(params.points, metadata);
    },
    toLine(params, range) {
        return toLine(params.points, range);
    },
    toServerTemplate(params) {
        return removeIndices(params.points);
    },
    fromServerTemplate(points) {
        return { points: assignIndices(points) };
    },
    movePoint(params, point) {
        return updateLinePoint(params.points, point);
    },
};

const unitStepFunctionRules = {
    init(metadata) {
        const params = {
            xStep: (metadata.statistics.max + metadata.statistics.min) / 2,
            yLeft: 0,
            yRight: 1,
        };
        return params;
    },
    getValue(xVal, params) {
        return xVal < params.xStep ? params.yLeft : params.yRight;
    },
    getRange(params, metadata) {
        const delta = (metadata.statistics.max - metadata.statistics.min) / 20;
        const interestingRange = [
            { id: v4(), x: params.xStep - delta, y: 0 },
            { id: v4(), x: params.xStep + delta, y: 0 },
        ];
        return getRangeForLinearApprox(interestingRange, metadata);
    },
    toLine(params, range) {
        const line = [
            { id: 'left', x: range.min, y: params.yLeft },
            {
                id: 'first',
                x: params.xStep,
                y: params.yLeft,
                mark: true,
                originalPointIndex: 0,
            },
            {
                id: 'second',
                x: params.xStep,
                y: params.yRight,
                mark: true,
                originalPointIndex: 1,
            },
            { id: 'right', x: range.max, y: params.yRight },
        ];
        if (range.min === params.xStep) {
            line.shift();
        }
        if (range.max === params.xStep) {
            line.pop();
        }
        return line;
    },
    toServerTemplate(params) {
        return [
            { x: params.xStep, y: params.yLeft },
            { x: params.xStep, y: params.yRight },
        ];
    },
    fromServerTemplate(points) {
        for (let i = 0; i < points.length - 1; ++i) {
            const point = points[i];
            const nextPoint = points[i + 1];
            if (point.x === nextPoint.x) {
                return {
                    yLeft: point.y,
                    yRight: nextPoint.y,
                    xStep: point.x,
                };
            }
        }
        throw new Error('wrong points set for the UniStep function');
    },
    movePoint(params, { originalPointIndex, x: newX, y: newY }) {
        const unistepParams = { ...params };
        const x = checkXBoundaries(newX);
        const y = checkYBoundaries(newY);
        if (originalPointIndex === 0 && unistepParams.yLeft < 1) {
            unistepParams.yLeft = y;
            if (y === 0) {
                unistepParams.yRight = 1;
            }
            else if (y === 1) {
                unistepParams.yRight = 0;
            }
        }
        else if (originalPointIndex === 1 && unistepParams.yRight < 1) {
            unistepParams.yRight = y;
            if (y === 0) {
                unistepParams.yLeft = 1;
            }
            else if (y === 1) {
                unistepParams.yLeft = 0;
            }
        }
        unistepParams.xStep = x;
        return unistepParams;
    },
};

const SCORING_FUNCTIONS = [
    {
        name: 'Discrete Properties',
        type: DesirabilityFunctionType.discrete,
    },
    {
        name: 'Linear',
        type: DesirabilityFunctionType.linear,
    },
    {
        name: 'Logarithmic',
        type: DesirabilityFunctionType.logarithmic,
    },
    {
        name: 'Rectangular',
        type: DesirabilityFunctionType.rectangular,
    },
    {
        name: 'Triangular',
        type: DesirabilityFunctionType.triangular,
    },
    {
        name: 'Unit Step',
        type: DesirabilityFunctionType.unitStep,
    },
    {
        name: 'Logistic',
        type: DesirabilityFunctionType.sigmoid,
    },
    {
        name: 'Custom Curve',
        type: DesirabilityFunctionType.custom,
    },
];
const SCORING_FUNCTIONS_RULES = {
    [DesirabilityFunctionType.linear]: linearFunctionRules,
    [DesirabilityFunctionType.unitStep]: unitStepFunctionRules,
    [DesirabilityFunctionType.triangular]: triangularFunctionRules,
    [DesirabilityFunctionType.rectangular]: rectangularFunctionRules,
    [DesirabilityFunctionType.logarithmic]: logarithmicFunctionRules,
    [DesirabilityFunctionType.sigmoid]: sigmoidFunctionRules,
    [DesirabilityFunctionType.custom]: customCurveFunctionRules,
    [DesirabilityFunctionType.discrete]: discreteFunctionRules,
};
const DEFAULT_COLORS = [
    '#e4744e',
    '#f2b06e',
    '#f6e652',
    '#98d268',
    '#489556',
    '#51a4aa',
    '#0093dc',
    '#5276bb',
    '#a376c2',
    '#92656e',
    '#ee7d87',
    '#b34454',
    '#bdba6f',
    '#e9f3a3',
    '#bed4a9',
    '#a0dbdd',
    '#c6f0fd',
    '#5041ad',
    '#bd9da4',
    '#828282',
];
const DEFAULT_COLORS_LENGTH = DEFAULT_COLORS.length;
function* getColor() {
    let i = 0;
    while (true) {
        yield DEFAULT_COLORS[i++];
        if (i === DEFAULT_COLORS_LENGTH) {
            i = 0;
        }
    }
}
const colorGenerator = getColor();
function initScoringFunctionsParams(metadata) {
    const functionParams = {
        [DesirabilityFunctionType.linear]: linearFunctionRules.init(metadata),
        [DesirabilityFunctionType.unitStep]: unitStepFunctionRules.init(metadata),
        [DesirabilityFunctionType.triangular]: triangularFunctionRules.init(metadata),
        [DesirabilityFunctionType.rectangular]: rectangularFunctionRules.init(metadata),
        [DesirabilityFunctionType.logarithmic]: logarithmicFunctionRules.init(metadata),
        [DesirabilityFunctionType.sigmoid]: sigmoidFunctionRules.init(metadata),
        [DesirabilityFunctionType.custom]: customCurveFunctionRules.init(metadata),
        [DesirabilityFunctionType.discrete]: discreteFunctionRules.init(metadata),
    };
    return functionParams;
}
function getDefaultScoringFunction(metadata) {
    const type = metadata.isDiscreteColumn
        ? DesirabilityFunctionType.discrete
        : DesirabilityFunctionType.linear;
    return {
        column: metadata.name,
        color: colorGenerator.next().value,
        isInUse: true,
        weight: 1,
        type,
        functionParams: initScoringFunctionsParams(metadata),
        measurementError: { type: 'none' },
        isDiscreteStringFunction: metadata.isDiscreteColumn && metadata.type === FieldType.String,
    };
}

function getCellValue(row, colId) {
    return row ? row[colId] : undefined;
}
/**
 * Note: the dataset may contain several or none structure columns, the function returns the first one
 */
const getStructureColumnId = (columnsInfo) => {
    return columnsInfo.find((col) => col.type === FieldType.Structure)?.name;
};
/**
 * Note: the dataset may contain several or none ID columns, the function returns the first one
 */
const getIdentifierColumnId = (columnsInfo) => {
    return columnsInfo.find((col) => col.type === FieldType.ID)?.name;
};

var CellBackgroundMode;
(function (CellBackgroundMode) {
    CellBackgroundMode["None"] = "None";
    CellBackgroundMode["Histogram"] = "Histogram";
    CellBackgroundMode["Heatmap"] = "Heatmap";
})(CellBackgroundMode || (CellBackgroundMode = {}));
var RowSizes;
(function (RowSizes) {
    RowSizes["SMALL"] = "small";
    RowSizes["MEDIUM"] = "medium";
    RowSizes["BIG"] = "big";
})(RowSizes || (RowSizes = {}));

var StructureType;
(function (StructureType) {
    StructureType[StructureType["SMILES"] = 0] = "SMILES";
    StructureType[StructureType["MOLFILE"] = 1] = "MOLFILE";
})(StructureType || (StructureType = {}));

var DTNodeType;
(function (DTNodeType) {
    DTNodeType["Property"] = "Property";
    DTNodeType["And"] = "And";
    DTNodeType["Or"] = "Or";
    DTNodeType["ALN"] = "AtLeastN";
    DTNodeType["AMX"] = "AtMostX";
    DTNodeType["Chart"] = "Chart";
    DTNodeType["Group"] = "Group";
    DTNodeType["StructureSearch"] = "StructureSearch";
    DTNodeType["Date"] = "Date";
})(DTNodeType || (DTNodeType = {}));
var DTOperation;
(function (DTOperation) {
    DTOperation["Greater"] = ">";
    DTOperation["Less"] = "<";
    DTOperation["GreaterEqual"] = "\u2265";
    DTOperation["LessEqual"] = "\u2264";
})(DTOperation || (DTOperation = {}));
var ConditionType;
(function (ConditionType) {
    ConditionType["Simple"] = "simple";
    ConditionType["Range"] = "range";
    ConditionType["Discrete"] = "discrete";
    ConditionType["DesirabilityFunction"] = "desirabilityFunction";
})(ConditionType || (ConditionType = {}));
var PortGroupingType;
(function (PortGroupingType) {
    PortGroupingType["Regular"] = "regular";
    PortGroupingType["TrueMissing"] = "trueMissing";
    PortGroupingType["FalseMissing"] = "falseMissing";
    PortGroupingType["HideMissing"] = "hideMissing";
})(PortGroupingType || (PortGroupingType = {}));
function isPropertyNode(node) {
    return node.type === DTNodeType.Property;
}
function isDateNode(node) {
    return node.type === DTNodeType.Date;
}
function isOrNode(node) {
    return node.type === DTNodeType.Or;
}
function isAndNode(node) {
    return node.type === DTNodeType.And;
}
function isAtLeastNNode(node) {
    return node.type === DTNodeType.ALN;
}
function isAtMostXNode(node) {
    return node.type === DTNodeType.AMX;
}
function isChartNode(node) {
    return node.type === DTNodeType.Chart;
}
function isStructSearchNode(node) {
    return node.type === DTNodeType.StructureSearch;
}
function isPropertyLikeNode(node) {
    return (node.type === DTNodeType.Property ||
        node.type === DTNodeType.Date ||
        node.type === DTNodeType.StructureSearch);
}
function isGroupNode(node) {
    return node.type === DTNodeType.Group;
}

function isPropertyNodeGroups(node) {
    return node.type === DTNodeType.Property;
}
function isOrNodeGroups(node) {
    return node.type === DTNodeType.Or;
}
function isAndNodeGroups(node) {
    return node.type === DTNodeType.And;
}

function isPropertyNodeResult(node) {
    return node.type === DTNodeType.Property;
}
function isDateNodeResult(node) {
    return node.type === DTNodeType.Date;
}
function isStructureNodeResult(node) {
    return node.type === DTNodeType.StructureSearch;
}
function isPropertyLikeNodeResult(node) {
    return (node.type === DTNodeType.Property ||
        node.type === DTNodeType.Date ||
        node.type === DTNodeType.StructureSearch);
}
function isOrNodeResult(node) {
    return node.type === DTNodeType.Or;
}
function isAndNodeResult(node) {
    return node.type === DTNodeType.And;
}
function isAtLeastNNodeResult(node) {
    return node.type === DTNodeType.ALN;
}
function isAtMostXNodeResult(node) {
    return node.type === DTNodeType.AMX;
}
function isChartNodeResult(node) {
    return node.type === DTNodeType.Chart;
}

function createHistogramBars(statistics) {
    const { min, max, histogramStep = 1, // todo temporary fix, wait for BE
     } = statistics;
    return statistics.histogram.map((yVal, index, arr) => {
        const bar = {
            x0: min + histogramStep * index,
            x: index === arr.length - 1 ? max : min + histogramStep * (index + 1),
            y0: 0,
            y: arr.length !== 0 ? yVal : yVal || 1, // todo temporary fix, wait for BE
        };
        return bar;
    });
}

const RADAR_COLUMN_ID = 'radar';
const SCORE_COLUMN_ID = 'score';
const MISSING_VALUES_COLUMN_ID = 'missingColumns';
const COMPARISON_SCORE_COLUMN_ID = 'comparisonScore';
const CID = 'CID';
const ROW_NUMBER_COLUMN_ID = '__key';
const NEW_CALC_COLUMN_ID = 'new_calc_key';
const PAGE_SIZE = 100;
const MAX_DATASET = 30000;
const DEFAULT_COLUMN_NAMES = {
    [SCORE_COLUMN_ID]: 'Scoring Profile',
    [COMPARISON_SCORE_COLUMN_ID]: 'Comparison scoring',
    [MISSING_VALUES_COLUMN_ID]: 'Missing values',
    [RADAR_COLUMN_ID]: 'Radar',
    [CID]: 'CID',
};

export { CID, COLUMN_TYPE_READABLE_NAME, COMPARISON_SCORE_COLUMN_ID, CellBackgroundMode, ConditionType, DEFAULT_COLORS, DEFAULT_COLUMN_NAMES, DTNodeType, DTOperation, DatasetMutColumnType, DesirabilityFunctionType, EMPTY_DECISION_TREE, EMoosaSarConfigImageSize, ExpressionType, FieldType, JobStatus, MAX_DATASET, MISSING_VALUES_COLUMN_ID, MMPView, MoosaDataContext, NEW_CALC_COLUMN_ID, PAGE_SIZE, PortGroupingType, RADAR_COLUMN_ID, ROW_NUMBER_COLUMN_ID, ReasonType, ResponseErrors, RowSizes, SCORE_COLUMN_ID, SCORING_FUNCTIONS, SCORING_FUNCTIONS_RULES, SarMatrixCellView, SortFunctionTableType, SpecialColumn, StructureType, createHistogramBars, customCurveFunctionRules, discreteFunctionRules, getCellValue, getColor, getDefaultScoringFunction, getIdentifierColumnId, getRawScoringFactorByColumnId, getRawScoringFactorsWithColumnIds, getRowSelectionKey, getScoringFactorByColumnId, getStructureColumnId, getSubstanceScoreValue, initScoringFunctionsParams, isAndNode, isAndNodeGroups, isAndNodeResult, isAtLeastNNode, isAtLeastNNodeResult, isAtMostXNode, isAtMostXNodeResult, isChartNode, isChartNodeResult, isColumnOfType, isDateNode, isDateNodeResult, isGroupNode, isNumericColumn, isOrNode, isOrNodeGroups, isOrNodeResult, isPossiblyComputedColumn, isPossiblyDiscreteColumn, isPropertyLikeNode, isPropertyLikeNodeResult, isPropertyNode, isPropertyNodeGroups, isPropertyNodeResult, isScoringColumn, isScoringType, isStructSearchNode, isStructureNodeResult, linearFunctionRules, logarithmicFunctionRules, rectangularFunctionRules, sigmoidFunctionRules, triangularFunctionRules, unitStepFunctionRules, useMoosaDataContext };
//# sourceMappingURL=index.js.map
